import React from 'react';

const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-primary-500 text-secondary-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">About Us</h1>
            <p className="text-xl md:text-2xl text-secondary-100 max-w-3xl mx-auto">
              Engineering excellence through innovative solutions and
              collaborative partnerships
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Image Section - Left side on desktop, top on mobile */}
          <div className="lg:sticky lg:top-8 order-1 lg:order-1 animate-fade-in-left">
            <div className="bg-gray-200 rounded-lg shadow-lg aspect-[4/3] flex items-center justify-center hover:shadow-xl transition-shadow duration-300">
              <div className="text-center text-gray-500">
                <div className="w-16 md:w-24 h-16 md:h-24 mx-auto mb-4 bg-gray-300 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 md:w-12 h-8 md:h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-base md:text-lg font-medium">
                  Image Placeholder
                </p>
                <p className="text-xs md:text-sm">
                  Company image will be displayed here
                </p>
              </div>
            </div>
          </div>

          {/* Content Section - Right side on desktop, bottom on mobile */}
          <div className="space-y-6 md:space-y-8 order-2 lg:order-2 animate-fade-in-right">
            {/* Company Overview */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.1s' }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-primary-500 mb-4 md:mb-6">
                Harmony Engineering Solution LLC
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                Harmony Engineering Solution LLC – has been serving the needs of
                companies in projects such as Design Engineering, Steel
                fabrication, Machining and Plant maintenance. Our commitment to
                delivering high-end engineered metal products to a wide range of
                industries, including new and existing Oil & Gas refinery,
                Construction Industry, Process Industry, Food Industries,
                Manufacturing company, Fabrication Company etc., has been the
                cornerstone of continued success.
              </p>
            </div>

            {/* Our Vision */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.2s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-3 md:mb-4">
                Our Vision
              </h3>
              <div className="space-y-2 md:space-y-3 text-gray-700 text-sm md:text-base">
                <p>
                  To increase our market value by producing leadership with our
                  positive approach in any conditions
                </p>
                <p>
                  Partner with client internal and external to produce success
                  for all
                </p>
                <p>
                  We wish to implement high level of commitment towards customer
                  satisfaction by performing at peak always.
                </p>
              </div>
            </div>

            {/* Mission */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.3s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-3 md:mb-4">
                Mission
              </h3>
              <div className="space-y-2 md:space-y-3 text-gray-700 text-sm md:text-base">
                <p>
                  Working as a team towards our motto: 'we partner your
                  success'.
                </p>
                <p>
                  We consider our customer most valued and only power source.
                </p>
                <p>
                  We provide solutions with top quality workmanship, time
                  commitment.
                </p>
              </div>
            </div>

            {/* Core Values */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.4s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-3 md:mb-4">
                Core Values
              </h3>
              <ul className="space-y-1 md:space-y-2 text-gray-700 text-sm md:text-base">
                {[
                  'Positive Approach',
                  'Respect',
                  'Leadership',
                  'Accountability',
                  'Integrity',
                  'Quality',
                ].map((value, index) => (
                  <li
                    key={index}
                    className="flex items-center space-x-2 animate-fade-in hover:translate-x-1 transition-transform duration-200"
                    style={{ animationDelay: `${0.5 + index * 0.1}s` }}
                  >
                    <span className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0"></span>
                    <span>{value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-primary-500 text-secondary-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Partner with Us?</h2>
          <p className="text-xl text-secondary-100 mb-8 max-w-2xl mx-auto">
            Let's work together to bring your engineering projects to life with
            our expertise and commitment to excellence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-secondary-500 text-primary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-100 transition-colors cursor-pointer"
            >
              Contact Us
            </a>
            <a
              href="/services"
              className="border-2 border-secondary-500 text-secondary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-500 hover:text-primary-500 transition-colors cursor-pointer"
            >
              Our Services
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
